import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { formatoFechaBolivia } from '@/lib/utils/formato-precio';
import type { EntradaDelHistorial, PaginaDelHistorial } from '@/types/historial.types';

/**
 * Spec 294 (D-R4-5): el historial de cambios de una estrategia o de una regla.
 * Pide de a 20, lo más nuevo primero; «Ver más» sigue por cursor (`antesDe` =
 * el `siguiente` de la página anterior) y no repite ninguna. La entrada de la
 * creación que anotó la migración avisa desde cuándo hay registro.
 */

const NO_SE_PUDO = 'No se pudo traer el historial.';

/** Un pedido que tira en el acto también es un error (no rompe la pantalla). */
const pedirSinTirar = (pedir: () => Promise<PaginaDelHistorial>) => Promise.resolve().then(pedir);

const registroDesdeDe = (e: EntradaDelHistorial): string | null => {
  const d = e.detalle as { relleno?: unknown; registroDesde?: unknown } | null;
  return d && d.relleno === true && typeof d.registroDesde === 'string' ? d.registroDesde : null;
};

export function HistorialDeCambios({
  pedir,
  enPalabras,
  version = 0,
}: {
  /** null = la primera página; si no, el cursor de «Ver más». */
  pedir: (antesDe: number | null) => Promise<PaginaDelHistorial>;
  /** Todas las entradas cargadas van de segundo argumento (lo más nuevo primero). */
  enPalabras: (
    e: EntradaDelHistorial,
    todas: EntradaDelHistorial[]
  ) => { titulo: string; lineas: string[] };
  /** Cambiarla vuelve a pedir desde el principio. */
  version?: number;
}) {
  const [entradas, setEntradas] = useState<EntradaDelHistorial[]>([]);
  const [siguiente, setSiguiente] = useState<number | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [errorMas, setErrorMas] = useState(false);
  // Una respuesta vieja (de otra versión) no pisa la nueva.
  const turno = useRef(0);
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;

  const cargar = useCallback(async () => {
    const mio = ++turno.current;
    setCargando(true);
    setError(false);
    setErrorMas(false);
    // Ronda final de R4: un «Ver más» en vuelo queda viejo; si no, el botón quedaba trabado.
    setCargandoMas(false);
    try {
      const p = await pedirSinTirar(() => pedirRef.current(null));
      if (mio !== turno.current) return;
      setEntradas(p.entradas);
      setSiguiente(p.siguiente);
    } catch {
      if (mio !== turno.current) return;
      setError(true);
    } finally {
      if (mio === turno.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar, version]);

  const verMas = async () => {
    if (siguiente == null || cargandoMas) return;
    const mio = turno.current;
    setCargandoMas(true);
    setErrorMas(false);
    try {
      const p = await pedirSinTirar(() => pedirRef.current(siguiente));
      if (mio !== turno.current) return;
      setEntradas((ya) => {
        const vistas = new Set(ya.map((e) => e.id));
        return [...ya, ...p.entradas.filter((e) => !vistas.has(e.id))];
      });
      setSiguiente(p.siguiente);
    } catch {
      if (mio === turno.current) setErrorMas(true);
    } finally {
      if (mio === turno.current) setCargandoMas(false);
    }
  };

  if (cargando)
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
      </div>
    );
  if (error) return <EstadoError onReintentar={() => void cargar()}>{NO_SE_PUDO}</EstadoError>;
  if (entradas.length === 0)
    return <p className="text-sm text-muted-foreground">Todavía no hay cambios.</p>;

  const registroDesde = entradas.map(registroDesdeDe).find((x) => x != null) ?? null;

  return (
    <div className="space-y-3">
      <ol aria-label="Historial de cambios" className="space-y-3">
        {entradas.map((e) => {
          const { titulo, lineas } = enPalabras(e, entradas);
          return (
            <li key={e.id} className="min-w-0 border-l-2 border-border pl-3">
              <p className="min-w-0 font-medium [overflow-wrap:anywhere]">{titulo}</p>
              {lineas.map((l, i) => (
                <p key={i} className="min-w-0 text-sm [overflow-wrap:anywhere]">
                  {l}
                </p>
              ))}
              <p className="text-xs text-muted-foreground">
                {formatoFechaBolivia(e.cuando, { hora: true })}
              </p>
            </li>
          );
        })}
      </ol>
      {errorMas && <EstadoError onReintentar={() => void verMas()}>{NO_SE_PUDO}</EstadoError>}
      {siguiente != null && !errorMas && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" disabled={cargandoMas} onClick={() => void verMas()}>
            Ver más
          </Button>
        </div>
      )}
      {registroDesde && siguiente == null && (
        <p className="text-xs text-muted-foreground">
          Lo que cambió antes del {formatoFechaBolivia(registroDesde, { hora: true })} no quedó
          registrado.
        </p>
      )}
    </div>
  );
}
